import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import Header from '../components/Header'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [isValidSession, setIsValidSession] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session) {
        setIsValidSession(true)
      } else {
        setMessage({ 
          type: 'error', 
          text: 'El enlace ha expirado o es inválido. Por favor solicita un nuevo enlace de recuperación.' 
        })
      }
      setCheckingSession(false)
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true)
        setCheckingSession(false)
      }
    })

    checkSession()

    return () => subscription.unsubscribe()
  }, [])

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setMessage({ type: '', text: '' })

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' })
      return
    }

    if (password.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' })
      return
    }

    setLoading(true)

    const { error } = await supabase.auth.updateUser({ password })

    if (error) {
      setMessage({ type: 'error', text: 'Error al actualizar la contraseña. Por favor intenta de nuevo.' })
    } else {
      setMessage({ type: 'success', text: '¡Contraseña actualizada exitosamente! Redirigiendo...' })
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    }

    setLoading(false)
  }

  if (checkingSession) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 68px)',
          marginTop: '68px',
          background: 'var(--background)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '3px solid var(--brand-blue-200)',
              borderTopColor: 'var(--brand-blue)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{
              fontFamily: "'Montserrat', sans-serif",
              color: 'var(--textMuted)'
            }}>Verificando enlace...</p>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Restablecer Contraseña - Plaza Aurora</title>
      </Helmet>

      <Header />

      <div style={{
        minHeight: 'calc(100vh - 68px)',
        marginTop: '68px',
        background: 'var(--background)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)',
          padding: '2.5rem',
          width: '100%',
          maxWidth: '440px'
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <h1 style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'var(--displayXL)',
              fontWeight: 'var(--bold)',
              color: 'var(--textPrimary)',
              margin: '0 0 0.5rem 0',
              lineHeight: '1.1'
            }}>
              Plaza Aurora
            </h1>
            <p style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'var(--body)',
              color: 'var(--textMuted)',
              margin: 0
            }}>
              Restablecer Contraseña
            </p>
          </div>

          {!isValidSession ? (
            <div style={{ textAlign: 'center' }}>
              {message.text && (
                <div style={{
                  background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
                  color: '#DC2626',
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--small)',
                  border: '1px solid #FCA5A5',
                  marginBottom: '1.5rem'
                }}>
                  {message.text}
                </div>
              )}
              <button
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.5rem',
                  background: 'var(--brand-blue)',
                  color: 'white',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--body)',
                  fontWeight: 'var(--bold)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                Volver al Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem'
            }}>
              <p style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 'var(--small)',
                color: 'var(--textMuted)',
                margin: 0,
                textAlign: 'center'
              }}>
                Ingresa tu nueva contraseña. Debe tener al menos 6 caracteres.
              </p>

              {/* New Password */}
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--small)',
                  fontWeight: 'var(--medium)',
                  color: 'var(--textPrimary)',
                  marginBottom: '0.5rem'
                }}>
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '1px solid var(--mutedStroke)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 'var(--body)',
                    color: 'var(--textPrimary)',
                    background: 'var(--surface)',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.outline = 'none'
                    e.currentTarget.style.borderColor = 'var(--brand-blue)'
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--small)',
                  fontWeight: 'var(--medium)',
                  color: 'var(--textPrimary)',
                  marginBottom: '0.5rem'
                }}>
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '1px solid var(--mutedStroke)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 'var(--body)',
                    color: 'var(--textPrimary)',
                    background: 'var(--surface)',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.outline = 'none'
                    e.currentTarget.style.borderColor = 'var(--brand-blue)'
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Message */}
              {message.text && (
                <div style={{
                  background: message.type === 'success' 
                    ? 'linear-gradient(135deg, #D1FAE5 0%, #ECFDF5 100%)' 
                    : 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
                  color: message.type === 'success' ? '#059669' : '#DC2626',
                  padding: '0.875rem 1rem',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--small)',
                  border: `1px solid ${message.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`
                }}>
                  {message.text}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.5rem',
                  background: loading ? 'var(--textMuted)' : 'var(--brand-blue)',
                  color: 'white',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--body)',
                  fontWeight: 'var(--bold)',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }
                }}
              >
                {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
              </button>

              {/* Back to Login */}
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.5rem',
                  background: 'transparent',
                  color: 'var(--textPrimary)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'var(--body)',
                  fontWeight: 'var(--medium)',
                  border: '1px solid var(--mutedStroke)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--brand-blue-50)'
                  e.currentTarget.style.borderColor = 'var(--brand-blue)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                }}
              >
                ← Volver al Login
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  )
}
